/**
 * Horarios › Cuadro de turnos (008 · T045 · US1; contracts/ui.md §1.2, FR-004–FR-006).
 *
 * - Filtros en borrador (QA-2): sede (obligatoria; arranca en la primera del alcance), desde/hasta
 *   (≤ 42 días; arranca en el mes en curso **en la zona de la sede**), trabajador y búsqueda, con
 *   "Buscar" y "Actualizar". La API recorta por alcance: el encargado solo recibe sus sedes y sus
 *   trabajadores.
 * - Matriz navegable por teclado (`schedule-grid.tsx`) y barra de acción con la selección
 *   (`grid-action-bar.tsx`). Guardar = un `POST /schedule-grid/cells` con `base_assignment_id` de
 *   lo que se vio (control de edición concurrente); todo o nada.
 * - Errores por celda (`details.issues[].path = cells.N.…` o `details.field = cells[N]`): borde
 *   Error + icono en la celda y panel "N días no se guardaron". `409 SCHEDULE_CHANGED`: toast "Este
 *   día cambió mientras lo editabas" y recarga del cuadro.
 * - Solo lectura: sin `schedules.write` o con la empresa suspendida no hay selección ni barra; las
 *   celdas con candado (período cerrado, integración, fuera de la relación laboral) tampoco se
 *   seleccionan. El historial (clic derecho o Enter) siempre está disponible.
 */
import { AlertCircle, CalendarClock, MapPin, Users } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import {
  SCHEDULE_GRID_MAX_DAYS,
  type GridCell as GridCellData,
  type SaveGridCell,
  type Site,
} from '@fermin/contracts';

import { Button } from '../../../components/ui/button';
import { Card, CardDescription, CardTitle } from '../../../components/ui/card';
import { Combobox } from '../../../components/ui/combobox';
import { EmptyState } from '../../../components/ui/empty-state';
import { Field } from '../../../components/ui/field';
import { FilterActions, FilterForm } from '../../../components/ui/filter-actions';
import { Input } from '../../../components/ui/input';
import { RefreshButton } from '../../../components/ui/refresh-button';
import { Select } from '../../../components/ui/select';
import { SkeletonLines } from '../../../components/ui/skeleton';
import { useToast } from '../../../components/ui/toast';
import { t } from '../../../i18n';
import { useAppliedFilters } from '../../../lib/use-applied-filters';
import { hasPermission, useMe } from '../../shell/use-me';
import { useReadOnly } from '../../shell/use-read-only';
import { useSites } from '../../sites/api';
import { flattenWorkerPages, useWorkers } from '../../workers/api';
import {
  isScheduleChanged,
  scheduleErrorMessage,
  scheduleGridKey,
  useInvalidateScheduleGrid,
  useSaveGridCells,
  useScheduleGrid,
  useShiftTemplates,
  type GridCellRef,
  type ScheduleGridFilters,
} from '../api';
import { formatLocalDay, monthBounds, rangeLength, todayInZone } from '../format';

import { CellHistoryPopover } from './cell-history-popover';
import { GridActionBar, type GridAction } from './grid-action-bar';
import { ScheduleGrid, type ScheduleGridRow } from './schedule-grid';
import { cellKey, parseCellKey, useGridSelection } from './use-grid-selection';

import type { ApiError } from '../../../api/client';

interface GridDraft {
  site_id: string;
  from_day: string;
  to_day: string;
  worker_id: string;
  search: string;
}

/** Texto del problema del rango, o `null` si es válido (≤ 42 días, desde ≤ hasta). */
export function gridRangeError(from: string, to: string): string | null {
  if (!from || !to) {
    return t('schedules.grid.filters.rangeRequired');
  }
  const length = rangeLength(from, to);
  if (length < 1) {
    return t('schedules.grid.filters.rangeInverted');
  }
  if (length > SCHEDULE_GRID_MAX_DAYS) {
    return t('schedules.grid.filters.rangeTooLong', { max: SCHEDULE_GRID_MAX_DAYS });
  }
  return null;
}

/** Índice de celda de un path de error (`cells.3.template_ids`, `cells[3]`). */
export function cellIndexFromPath(path: unknown): number | null {
  if (typeof path !== 'string') {
    return null;
  }
  const match = /^cells(?:\.|\[)(\d+)/.exec(path);
  return match ? Number(match[1]) : null;
}

/**
 * Errores por celda de un fallo de guardado: `details.issues[]` (400) y `details.field` (409). Las
 * claves salen del orden del cuerpo enviado.
 */
export function cellErrorsFrom(
  error: ApiError,
  sent: readonly SaveGridCell[],
): Map<string, string> {
  const errors = new Map<string, string>();
  const keyOf = (index: number | null) => {
    const cell = index === null ? undefined : sent[index];
    return cell ? cellKey(cell.worker_id, cell.day) : null;
  };
  const issues = error.details?.['issues'];
  if (Array.isArray(issues)) {
    for (const issue of issues as { path?: unknown; message?: unknown }[]) {
      const key = keyOf(cellIndexFromPath(issue.path));
      if (key && !errors.has(key)) {
        errors.set(
          key,
          typeof issue.message === 'string' ? issue.message : scheduleErrorMessage(error),
        );
      }
    }
  }
  const fieldKey = keyOf(cellIndexFromPath(error.details?.['field']));
  if (fieldKey && !errors.has(fieldKey)) {
    errors.set(fieldKey, scheduleErrorMessage(error));
  }
  return errors;
}

export interface GridTabProps {
  /** "Ir a Turnos" desde el estado vacío. */
  onGoToTemplates?: () => void;
}

export function GridTab({ onGoToTemplates }: GridTabProps) {
  const sites = useSites({ status: 'ACTIVE' });

  if (sites.isPending) {
    return <SkeletonLines lines={6} />;
  }
  if (sites.isError) {
    return (
      <Card>
        <CardTitle>{t('schedules.grid.error.title')}</CardTitle>
        <CardDescription>{t('schedules.grid.error.description')}</CardDescription>
        <Button className="mt-fm-4" size="sm" onClick={() => void sites.refetch()}>
          {t('common.buttons.retry')}
        </Button>
      </Card>
    );
  }
  if (sites.data.length === 0) {
    return (
      <EmptyState
        icon={MapPin}
        title={t('schedules.grid.noSites.title')}
        description={t('schedules.grid.noSites.description')}
      />
    );
  }
  return <GridWorkspace sites={sites.data} onGoToTemplates={onGoToTemplates} />;
}

function initialDraft(site: Site): GridDraft {
  const { from, to } = monthBounds(todayInZone(site.timezone));
  return { site_id: site.id, from_day: from, to_day: to, worker_id: '', search: '' };
}

function GridWorkspace({
  sites,
  onGoToTemplates,
}: {
  sites: readonly Site[];
  onGoToTemplates?: (() => void) | undefined;
}) {
  const me = useMe();
  const { readOnly } = useReadOnly();
  const { toast } = useToast();
  const canWrite = hasPermission(me.data, 'schedules.write') && !readOnly;
  const canConfigure = hasPermission(me.data, 'schedules.configure');

  const [defaults] = useState(() => initialDraft(sites[0]!));
  const { draft, applied, update, apply, clear, dirty, canClear } =
    useAppliedFilters<GridDraft>(defaults);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [cellErrors, setCellErrors] = useState<ReadonlyMap<string, string>>(new Map());
  const [history, setHistory] = useState<GridCellRef | null>(null);

  const site = sites.find((entry) => entry.id === applied.site_id) ?? sites[0]!;
  const timeZone = site.timezone;

  const filters: ScheduleGridFilters | null = useMemo(() => {
    if (gridRangeError(applied.from_day, applied.to_day) !== null) {
      return null;
    }
    return {
      from_day: applied.from_day,
      to_day: applied.to_day,
      site_id: applied.site_id,
      ...(applied.worker_id ? { worker_id: applied.worker_id } : {}),
      ...(applied.search.trim() ? { search: applied.search.trim() } : {}),
    };
  }, [applied]);

  const query = useScheduleGrid(filters);
  const templates = useShiftTemplates('ACTIVE');
  const save = useSaveGridCells();
  const invalidateGrid = useInvalidateScheduleGrid();

  const days = useMemo(() => query.data?.pages[0]?.days ?? [], [query.data]);
  const rows: ScheduleGridRow[] = useMemo(
    () => query.data?.pages.flatMap((page) => page.rows) ?? [],
    [query.data],
  );
  const cellsByKey = useMemo(() => {
    const map = new Map<string, GridCellData>();
    for (const row of rows) {
      for (const cell of row.cells) {
        map.set(cellKey(row.worker.id, cell.day), cell);
      }
    }
    return map;
  }, [rows]);
  const workerNames = useMemo(
    () => new Map(rows.map((row) => [row.worker.id, row.worker.name])),
    [rows],
  );

  const isSelectable = useCallback(
    (key: string) => {
      if (!canWrite) {
        return false;
      }
      const cell = cellsByKey.get(key);
      return cell !== undefined && cell.locked === null;
    },
    [canWrite, cellsByKey],
  );

  const rowIds = useMemo(() => rows.map((row) => row.worker.id), [rows]);
  const dayIds = useMemo(() => days.map((day) => day.day), [days]);
  const selection = useGridSelection({ rows: rowIds, days: dayIds, isSelectable });
  const selectedKeys = useMemo(
    () => [...selection.selected].filter((key) => isSelectable(key)),
    [selection.selected, isSelectable],
  );

  const workers = useWorkers(draft.site_id ? { site_id: draft.site_id } : {});
  const workerOptions = useMemo(
    () => [
      { value: '', label: t('schedules.grid.filters.allWorkers') },
      ...flattenWorkerPages(workers.data).map((worker) => {
        const name = `${worker.first_names} ${worker.last_names}`.trim();
        return {
          value: worker.id,
          label: worker.internal_code ? `${name} · ${worker.internal_code}` : name,
        };
      }),
    ],
    [workers.data],
  );
  const siteOptions = useMemo(
    () => sites.map((entry) => ({ value: entry.id, label: entry.name })),
    [sites],
  );

  function applyFilters() {
    const problem = gridRangeError(draft.from_day, draft.to_day);
    setRangeError(problem);
    if (problem !== null) {
      return;
    }
    setCellErrors(new Map());
    selection.clear();
    apply();
  }

  function clearFilters() {
    setRangeError(null);
    setCellErrors(new Map());
    selection.clear();
    clear();
  }

  function saveCells(action: GridAction) {
    const body: SaveGridCell[] = selectedKeys.map((key) => {
      const { workerId, day } = parseCellKey(key);
      const cell = cellsByKey.get(key);
      return {
        worker_id: workerId,
        day,
        kind: action.kind,
        site_id: site.id,
        template_ids: action.kind === 'SHIFT' ? action.templateIds : [],
        base_assignment_id: cell?.assignment_id ?? null,
      };
    });
    if (body.length === 0) {
      return;
    }
    setCellErrors(new Map());
    save.mutate(
      { cells: body },
      {
        onSuccess: (result) => {
          toast({
            title:
              result.unchanged > 0
                ? t('schedules.grid.toast.savedWithUnchanged', {
                    saved: result.saved,
                    unchanged: result.unchanged,
                  })
                : t('schedules.grid.toast.saved', { saved: result.saved }),
            variant: 'success',
          });
          selection.clear();
        },
        onError: (error) => {
          const errors = cellErrorsFrom(error, body);
          setCellErrors(errors);
          if (isScheduleChanged(error)) {
            toast({ title: t('schedules.errors.SCHEDULE_CHANGED'), variant: 'warning' });
            void invalidateGrid();
            return;
          }
          if (errors.size === 0) {
            toast({ title: scheduleErrorMessage(error), variant: 'error' });
          }
        },
      },
    );
  }

  const activeTemplates = templates.data ?? [];
  const gridHasPlans = rows.some((row) => row.cells.some((cell) => cell.kind !== null));
  const showNoTemplates =
    templates.isSuccess && activeTemplates.length === 0 && query.isSuccess && !gridHasPlans;
  const singleSelected = selectedKeys.length === 1 ? parseCellKey(selectedKeys[0]!) : null;

  return (
    <div className="flex flex-col gap-fm-4">
      <div className="flex flex-wrap items-center justify-between gap-fm-3">
        <p className="text-fm-sm text-fm-text-secondary">
          {t('schedules.grid.description')}{' '}
          <span className="text-fm-xs" data-testid="schedule-grid-timezone">
            {t('schedules.grid.timezoneHint', { timezone: timeZone })}
          </span>
        </p>
        <RefreshButton query={query} pagesKey={scheduleGridKey(filters)} />
      </div>

      <Card>
        <FilterForm onApply={applyFilters} className="block">
          <fieldset className="flex flex-wrap items-end gap-fm-3">
            <legend className="sr-only">{t('schedules.grid.filters.legend')}</legend>
            <Field label={t('schedules.grid.filters.site')} required className="w-[220px]">
              {(control) => (
                <Select
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  aria-required={control['aria-required']}
                  value={draft.site_id}
                  onValueChange={(value) => update({ site_id: value, worker_id: '' })}
                  options={siteOptions}
                />
              )}
            </Field>
            <Field label={t('schedules.grid.filters.fromDay')} className="w-[170px]">
              {(control) => (
                <Input
                  {...control}
                  type="date"
                  invalid={rangeError !== null}
                  value={draft.from_day}
                  onChange={(event) => update({ from_day: event.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('schedules.grid.filters.toDay')}
              className="w-[170px]"
              error={rangeError ?? undefined}
            >
              {(control) => (
                <Input
                  {...control}
                  type="date"
                  value={draft.to_day}
                  onChange={(event) => update({ to_day: event.target.value })}
                />
              )}
            </Field>
            <Field label={t('schedules.grid.filters.worker')} className="w-[240px]">
              {(control) => (
                <Combobox
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={draft.worker_id}
                  onValueChange={(value) => update({ worker_id: value })}
                  options={workerOptions}
                  placeholder={t('schedules.grid.filters.allWorkers')}
                  searchPlaceholder={t('schedules.grid.filters.workerSearch')}
                />
              )}
            </Field>
            <Field label={t('schedules.grid.filters.search')} className="w-[200px]">
              {(control) => (
                <Input
                  {...control}
                  type="search"
                  maxLength={120}
                  value={draft.search}
                  onChange={(event) => update({ search: event.target.value })}
                />
              )}
            </Field>
            <FilterActions
              onSearch={applyFilters}
              dirty={dirty}
              canClear={canClear}
              onClear={clearFilters}
            />
          </fieldset>
        </FilterForm>
      </Card>

      {query.isPending && filters !== null ? <SkeletonLines lines={6} /> : null}

      {query.isError ? (
        <Card>
          <CardTitle>{t('schedules.grid.error.title')}</CardTitle>
          <CardDescription>{scheduleErrorMessage(query.error)}</CardDescription>
          <Button className="mt-fm-4" size="sm" onClick={() => void query.refetch()}>
            {t('common.buttons.retry')}
          </Button>
        </Card>
      ) : null}

      {showNoTemplates ? (
        <EmptyState
          icon={CalendarClock}
          title={t('schedules.grid.empty.title')}
          description={t('schedules.grid.empty.description')}
          action={
            canConfigure && onGoToTemplates ? (
              <Button variant="compact" size="md" onClick={onGoToTemplates}>
                {t('schedules.grid.empty.goToTemplates')}
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {query.isSuccess && rows.length === 0 && !showNoTemplates ? (
        <EmptyState
          icon={Users}
          title={t('schedules.grid.noWorkers.title')}
          description={t('schedules.grid.noWorkers.description')}
        />
      ) : null}

      {cellErrors.size > 0 ? (
        <div
          role="alert"
          data-testid="schedule-grid-errors"
          className="flex flex-col gap-fm-2 rounded-fm-md border border-fm-danger bg-fm-danger-soft p-fm-4 text-fm-sm text-fm-danger-text"
        >
          <p className="flex items-center gap-fm-2 font-semibold">
            <AlertCircle size={16} aria-hidden="true" />
            {t('schedules.grid.errors.title', { count: cellErrors.size })}
          </p>
          <p>{t('schedules.grid.errors.nothingSaved')}</p>
          <ul className="list-disc pl-fm-5">
            {[...cellErrors].map(([key, message]) => {
              const { workerId, day } = parseCellKey(key);
              return (
                <li key={key}>
                  {t('schedules.grid.errors.item', {
                    worker: workerNames.get(workerId) ?? workerId,
                    day: formatLocalDay(day),
                    message,
                  })}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {rows.length > 0 && !showNoTemplates ? (
        <>
          <p className="text-fm-xs text-fm-text-secondary">
            {canWrite ? t('schedules.grid.keyboardHint') : t('schedules.grid.readOnlyHint')}
          </p>
          <ScheduleGrid
            days={days}
            rows={rows}
            selection={selection}
            isSelectable={isSelectable}
            errors={cellErrors}
            onOpenHistory={(workerId, day) => setHistory({ workerId, day })}
          />
          {query.hasNextPage ? (
            <div className="flex justify-center">
              <Button
                size="sm"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common.buttons.loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {canWrite && selectedKeys.length > 0 ? (
        <GridActionBar
          count={selectedKeys.length}
          templates={activeTemplates}
          pending={save.isPending}
          onSave={saveCells}
          onClear={selection.clear}
          onHistory={singleSelected ? () => setHistory(singleSelected) : undefined}
        />
      ) : null}

      <CellHistoryPopover
        cell={history}
        workerName={history ? (workerNames.get(history.workerId) ?? '') : ''}
        timeZone={timeZone}
        onClose={() => setHistory(null)}
      />
    </div>
  );
}
